import noop from 'lodash/noop';
import forEach from 'lodash/forEach';
import styles from './html.module.scss';

// TODO: instead of re-exporting to alias, update consumers to reference @xmatters/common directly:
export {
  PAGE_RELAYOUT_EVENT,
  triggerCustomEvent,
  triggerResizeEvent,
} from '@xmatters/common/utils/html';

/**
 * @param {string} html - string that may contain some valid html
 * @returns {string} - escaped html
 */
export function escapeHtml(html) {
  return html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function unescapeHtml(string) {
  const map = {
    '&amp;amp;': '&',
    '&amp;': '&',
    '&amp;lt;': '<',
    '&lt;': '<',
    '&amp;gt;': '>',
    '&gt;': '>',
    '&amp;quot;': '"',
    '&quot;': '"',
    '&amp;#039;': "'",
    '&#039;': "'",
    '&amp;nbsp;': ' ',
    '&nbsp;': ' ',
    '&amp;#160;': ' ',
    '&#160;': ' ',
    '&amp;mdash;': '-',
    '&mdash;': '-',
  };

  return string.replace(
    /&(amp;amp;|amp;lt;|amp;quot;|amp;#039;|amp;nbsp;|amp;#160;|amp;mdash;|amp;gt;|amp;|lt;|gt;|quot;|#039;|nbsp;|#160;|mdash;)/g,
    m => map[m],
  );
}

/**
 * Similar to document.createTreeWalker but with an ability to detect current line number
 * @param {HTMLElement} root - root element to start with
 * @param {Function} isNodeLineBreaking - function to determine if an element is line-breaking
 * @param {Function} callback - callback that will be called for every node under (and including) root element
 */
export function treeWalker(root, isNodeLineBreaking, callback) {
  let node = root;
  let line = 0;
  while (node) {
    const result = callback(node, line);
    if (result === NodeFilter.FILTER_ACCEPT) {
      return;
    }
    if (node.firstChild && result !== NodeFilter.FILTER_REJECT) {
      node = node.firstChild;
    } else if (node === root) {
      node = null;
    } else {
      let currentNode = node;
      let hasNewLine = isNodeLineBreaking(node);
      while (currentNode && !currentNode.nextSibling) {
        currentNode = currentNode.parentNode;
        if (currentNode === root) {
          currentNode = null;
        } else {
          hasNewLine = hasNewLine || isNodeLineBreaking(currentNode);
        }
      }
      node = currentNode && currentNode.nextSibling;
      hasNewLine = node && (hasNewLine || isNodeLineBreaking(node));
      if (hasNewLine) {
        line += 1;
      }
    }
  }
}

/**
 * Same as Node.contains, but without IE bug when `innerNode` is a text node
 * @param {HTMLElement} ancestor - supposed ancestor
 * @param {HTMLElement} innerNode - supposed descendent node
 * @returns {boolean} does node `ancestor` contain `innerNode`?
 */
export function nodeContains(ancestor, innerNode) {
  if (ancestor === null || innerNode === null) {
    return false;
  }

  if (innerNode === ancestor) {
    return true;
  }

  let node = innerNode && innerNode.parentNode;
  while (node) {
    if (node === ancestor) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
}

export function removeAllNodes(parentNode) {
  while (parentNode.firstChild) {
    parentNode.removeChild(parentNode.firstChild);
  }
}

export function hideNode(node) {
  if (!node) return noop;

  const { display } = node.style;
  // eslint-disable-next-line no-param-reassign
  node.style.display = 'none';

  return function showNode() {
    // eslint-disable-next-line no-param-reassign
    node.style.display = display;
  };
}

function convertTextRangeToSelectionRange(textRange, theDocument = document) {
  textRange.pasteHTML(
    `<span class="${styles.invisiblePositionMarker}"></span>`,
  );
  const parentElement = textRange.parentElement();
  let startOffset = 0;
  for (let i = 0; i < parentElement.childNodes.length; i += 1) {
    const node = parentElement.childNodes[i];
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.classList.contains(styles.invisiblePositionMarker)
    ) {
      startOffset = i;
      parentElement.removeChild(node);
      break;
    }
  }
  const range = theDocument.createRange();
  range.setStart(parentElement, startOffset);
  range.setEnd(parentElement, startOffset);
  return range;
}

/**
 * creates Range from given position
 * If the browser can't create a range at that position, it will return a null
 *
 * @param x
 * @param y
 * @param theDocument
 * @returns {null|Range}
 */
export function createRangeFromPoint(x, y, theDocument = document) {
  if (typeof theDocument.caretPositionFromPoint !== 'undefined') {
    const pos = theDocument.caretPositionFromPoint(x, y);
    // Sometimes Firefox gets `null` when near top/bottom of document
    if (pos) {
      const range = theDocument.createRange();
      range.setStart(pos.offsetNode, pos.offset);
      range.setEnd(pos.offsetNode, pos.offset);
      return range;
    }
  }

  if (typeof theDocument.caretRangeFromPoint !== 'undefined') {
    return theDocument.caretRangeFromPoint(x, y);
  }

  // IE 11 support
  if (typeof theDocument.body.createTextRange !== 'undefined') {
    // first create a text range and try to move it to that location
    const textRange = theDocument.body.createTextRange();
    try {
      textRange.moveToPoint(x, y);
    } catch (ex) {
      // could not move to that point
      return null;
    }
    const textRangeEnd = textRange.duplicate();
    textRange.setEndPoint('EndToEnd', textRangeEnd);

    return convertTextRangeToSelectionRange(textRange, theDocument);
  }

  return null;
}

function splitStyleStr(stylesStr) {
  const style = (stylesStr || '').trim();
  if (style.length === 0) {
    return [];
  }
  const str = style.endsWith(';')
    ? style.substring(0, style.length - 1)
    : style;
  return str.split(';').map(item => {
    const parts = item.split(':');
    parts[0] = parts[0].trim(); // trim key
    return parts;
  });
}

export function mergeStyles(stylesStr1, stylesStr2) {
  const styles1 = splitStyleStr(stylesStr1);
  const styles2 = splitStyleStr(stylesStr2);
  let result = '';
  forEach(styles1, item => {
    const resItem = styles2.find(i => i[0] === item[0]) || item;
    result += `${resItem.join(':')}; `;
  });
  forEach(styles2, item => {
    if (!styles1.find(i => i[0] === item[0])) {
      result += `${item.join(':')}; `;
    }
  });

  return result;
}

export function getElemMiddleY(elem) {
  const boundingRect = elem.getBoundingClientRect();
  return (boundingRect.top + boundingRect.bottom) / 2;
}

export function containsHtmlComment(html, commentNodeFilter) {
  const fragment = document
    .createRange()
    .createContextualFragment(`<div>${html}</div>`);

  const nodeIterator = document.createNodeIterator(
    fragment.firstChild,
    NodeFilter.SHOW_COMMENT,
    commentNodeFilter
      ? {
          acceptNode(node) {
            return commentNodeFilter(node)
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_REJECT;
          },
        }
      : undefined,
  );
  const commentNode = nodeIterator.nextNode();
  return !!commentNode;
}

export function stripHtmlComments(html, commentNodeFilter) {
  const fragment = document
    .createRange()
    .createContextualFragment(`<div>${html}</div>`);

  const nodeIterator = document.createNodeIterator(
    fragment.firstChild,
    NodeFilter.SHOW_COMMENT,
    commentNodeFilter
      ? {
          acceptNode(node) {
            return commentNodeFilter(node)
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_REJECT;
          },
        }
      : undefined,
  );
  let currentNode = nodeIterator.nextNode();

  while (currentNode) {
    currentNode.parentNode.removeChild(currentNode);
    currentNode = nodeIterator.nextNode();
  }

  const resultHTML = fragment.firstChild.innerHTML;
  return resultHTML;
}

export function isConditionalCommentNode(node) {
  return (
    node.nodeValue.startsWith('[if ') && node.nodeValue.endsWith('<![endif]')
  );
}

export function getRelativeBounds(elem, containerElem) {
  let top = 0;
  let left = 0;
  let current = elem;
  while (current && current !== containerElem) {
    top += current.offsetTop;
    left += current.offsetLeft;
    current = current.offsetParent;
  }

  return {
    width: elem.offsetWidth,
    height: elem.offsetHeight,
    top,
    left,
  };
}
